/**
 * RouteTransitionGuard — suppresses the 404/NotFound flash during admin↔user
 * context switches.
 *
 * React Router can briefly match the wildcard `*` route while AccessGate
 * flips state or while a lazy admin chunk is still loading, producing a
 * jarring "Page not found" frame. This guard wraps the wildcard element
 * and renders a soft loading state when:
 *   - the `admin_navigation_grace` localStorage flag was set within 5s, OR
 *   - the URL starts with /admin (admin chunk likely still resolving).
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AccessLoading } from './AccessLoading';

const GRACE_MS = 5000;

const isWithinGrace = (): boolean => {
  try {
    const ts = localStorage.getItem('admin_navigation_grace');
    if (!ts) return false;
    const elapsed = Date.now() - parseInt(ts, 10);
    if (elapsed >= GRACE_MS) {
      localStorage.removeItem('admin_navigation_grace');
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

interface RouteTransitionGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const RouteTransitionGuard: React.FC<RouteTransitionGuardProps> = ({
  children,
  fallback,
}) => {
  const location = useLocation();
  const [, setTick] = useState(0);

  // While inside the grace window, re-evaluate every 250ms so we flip back
  // to children as soon as the admin route mounts (or the grace expires).
  useEffect(() => {
    if (!isWithinGrace() && !location.pathname.startsWith('/admin')) return;
    const id = setInterval(() => setTick((t) => t + 1), 250);
    const timeout = setTimeout(() => clearInterval(id), GRACE_MS + 250);
    return () => {
      clearInterval(id);
      clearTimeout(timeout);
    };
  }, [location.pathname]);

  const suppressNotFound = isWithinGrace() || location.pathname.startsWith('/admin');

  if (suppressNotFound) {
    return <>{fallback ?? <AccessLoading />}</>;
  }

  return <>{children}</>;
};

export default RouteTransitionGuard;
